import { z } from 'zod';

// Helper: treat "" and null as undefined
const emptyToUndefined = z.preprocess(
  (v) => (v === '' || v === null ? undefined : v),
  z.any()
);

// Rate schema for hourly packages
const rateSchema = z.object({
  roleId: z.coerce.number().int().positive('roleRequired'),
  rate: z.coerce.number().optional(),
});

// Hourly package schema (inline)
const hourlyPackageSchema = z.object({
  hours: z.preprocess(
    (val) => (val === '' || val == null || Number.isNaN(val) ? undefined : Number(val)),
    z.number().optional().nullable()
  ),
  price: z.preprocess(
    (val) => (val === '' || val == null || Number.isNaN(val) ? undefined : Number(val)),
    z.number().optional().nullable()
  ),
  overTimeHourlyCost: z.preprocess(
    (val) => (val === '' || val == null || Number.isNaN(val) ? undefined : Number(val)),
    z.number().optional().nullable()
  ),
  rates: z.array(rateSchema).optional().default([]),
});

// Success package schema (inline)
const successPackageSchema = z.object({
  advancePayment: emptyToUndefined.pipe(
    z.coerce.number().finite().nonnegative().optional()
  ),
  successPayment: emptyToUndefined.pipe(
    z.coerce.number().finite().nonnegative().optional()
  ),
});

export const BILLING_PERIOD_TYPES = ['WEEKLY', 'MONTHLY', 'QUARTER', 'SEMI_ANNUAL', 'YEARLY', 'INSTANCE', 'MANUAL'] as const;
export type BillingPeriodType = typeof BILLING_PERIOD_TYPES[number];

const billingPeriodItemSchema = z.object({
  startsAt: z.date().optional().nullable(),
  endsAt: z.date().optional().nullable(),
});

// Main contract schema
export const contractSchema = z
  .object({
    // The name is the contract's identifier in every list. It was previously
    // unvalidated, so clearing the field submitted an empty string, which the
    // payload builder turns into `null` — silently wiping the name. The FORMAT is
    // only checked when the name actually changes (in `useContractForm`), mirroring
    // the backend, so legacy names that predate the rule stay editable.
    name: z.string().trim().min(1, 'nameRequired'),
    description: z.string().optional().default(''),
    contactFace: z.string().optional().default(''),
    contactPhone: z.string().optional().default(''),
    contactEmail: z.string().optional().default(''),
    vidRabot: z.string().optional().default(''),
    sudOrgan: z.string().optional().default(''),

    type: z.enum(['HOURLY', 'SUCCESS_FEE', 'FIXED', 'PRO_BONO'], {
      required_error: 'typeRequired',
    }),

    reason: z
      .enum(['USTAV', 'DOVERNOST'], { required_error: 'reasonRequired' })
      .default('USTAV')
      .optional(),

    currency: z.enum(['UZS', 'USD']).default('USD'),
    dolarRate: z.number().optional(),

    // Financial fields
    price: emptyToUndefined.pipe(
      z.coerce.number().finite().nonnegative().optional()
    ),
    advancePayment: z.preprocess(
      (val) => {
        if (val === '' || val == null || Number.isNaN(val)) return undefined;
        const num = Number(val);
        return Number.isFinite(num) ? num : undefined;
      },
      z.number().nonnegative().optional()
    ),
    advancePaymentUnit: emptyToUndefined.pipe(
      z.enum(['usd', 'percent']).optional()
    ),
    claimedPrice: emptyToUndefined.pipe(
      z.coerce.number().finite().nonnegative().optional()
    ),

    // Hourly specific
    // `hourlyPackageId` only binds the optional TEMPLATE picker — the
    // contract always clones its own package, so this is never sent to
    // the backend (see onSubmit) and needs no validation of its own.
    hourlyPackageId: emptyToUndefined.pipe(
      z.coerce.number().int().positive().optional()
    ),
    periodId: emptyToUndefined.pipe(
      z.coerce.number().int().positive().optional()
    ),
    minAdvanceForHourly: emptyToUndefined.pipe(
      z.coerce.number().finite().nonnegative().optional()
    ),

    // Relations
    clientId: z.number({
      invalid_type_error: 'clientRequired',
      required_error: 'clientRequired',
    }),
    practiceAreaId: emptyToUndefined.pipe(
      z.coerce.number().int().positive()
    ),

    // Dates
    //
    // Clearing a picker emits `null` (see DateTimePicker.handleClear). `null` must
    // be rejected here rather than reaching `z.coerce.date()`, which would coerce
    // it to `new Date(null)` — 1970-01-01 — and silently save that as the start
    // date. Mapping it to undefined first makes the required check fire instead.
    startsAt: z.preprocess(
      (v) => (v === '' || v === null ? undefined : v),
      z.coerce.date({ errorMap: () => ({ message: 'startDateRequired' }) })
    ),
    // `endsAt` is optional: `emptyToUndefined` maps the cleared `null` to
    // undefined, which `.optional()` accepts. `onSubmit` then sends an explicit
    // `endsAt: null`, which the backend treats as "clear the end date".
    endsAt: emptyToUndefined.pipe(z.coerce.date().optional()),

    // Packages
    successPackage: successPackageSchema.optional().nullable(),
    hourlyPackage: hourlyPackageSchema.optional().nullable(),

    // Billing periods (HOURLY only)
    billingPeriodType: z.enum(BILLING_PERIOD_TYPES).optional(),
    billingPeriodCount: z.preprocess(
      (val) => (val === '' || val == null || Number.isNaN(val) ? undefined : Number(val)),
      z.number().int().positive().optional()
    ),
    billingPeriods: z.array(billingPeriodItemSchema).optional().default([]),
  })
  .superRefine((data, ctx) => {
    // Date validation
    if (data.endsAt && data.startsAt > data.endsAt) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['startsAt'],
        message: 'startDateCannotBeLaterThanEndDate',
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['endsAt'],
        message: 'endDateCannotBeEarlierThanStartDate',
      });
    }

    // FIXED contract validation
    if (data.type === 'FIXED') {
      if (data.price === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['price'],
          message: 'priceRequiredForFixed',
        });
      }
      if (data.advancePayment === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['advancePayment'],
          message: 'advancePaymentRequiredForFixed',
        });
      }
      if (!data.advancePaymentUnit) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['advancePaymentUnit'],
          message: 'paymentUnitRequiredForFixed',
        });
      }
      if (
        data.advancePayment !== undefined &&
        data.price !== undefined &&
        data.advancePayment > data.price
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['advancePayment'],
          message: 'advancePaymentCannotExceedTotalPrice',
        });
      }
    }

    // HOURLY contract validation
    else if (data.type === 'HOURLY') {
      // A contract always carries its own custom tariff package now —
      // the global dropdown is only an optional prefill template.
      const pkg = data?.hourlyPackage;
      if (!pkg) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['hourlyPackage'],
          message: 'hourlyPackageDetailsRequired',
        });
      } else {
        if (pkg.hours === undefined || pkg.hours === null || pkg.hours <= 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['hourlyPackage', 'hours'],
            message: 'hoursRequiredAndPositive',
          });
        }
        if (pkg.price === undefined || pkg.price === null || pkg.price <= 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['hourlyPackage', 'price'],
            message: 'priceRequiredAndPositive',
          });
        }
        if (pkg.overTimeHourlyCost === undefined || pkg.overTimeHourlyCost === null || pkg.overTimeHourlyCost <= 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['hourlyPackage', 'overTimeHourlyCost'],
            message: 'overtimeCostRequiredAndPositive',
          });
        }
      }

      if (data.advancePayment === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['advancePayment'],
          message: 'advancePaymentRequiredForHourly',
        });
      } else if (
        data.minAdvanceForHourly !== undefined &&
        data.advancePayment < data.minAdvanceForHourly
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['advancePayment'],
          message: 'advancePaymentMinimum',
        });
      }

      if (!data.billingPeriodType) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['billingPeriodType'],
          message: 'billingPeriodTypeRequiredForHourly',
        });
      }

      // INSTANCE contracts have no scheduled periods — the single period is
      // started/stopped manually after creation — so they are exempt from the
      // "at least one period" requirement (matches isBillingPeriodsComplete).
      if (
        data.billingPeriodType !== 'INSTANCE' &&
        (!data.billingPeriods || data.billingPeriods.length === 0)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['billingPeriods'],
          message: 'billingPeriodsRequiredForHourly',
        });
      } else if (
        data.billingPeriodType &&
        data.billingPeriodType !== 'INSTANCE' &&
        data.billingPeriods.some((p) => !p?.endsAt)
      ) {
        // The CONTRACT end date is optional, but every generated/added period
        // still needs its own concrete end date to be submitted.
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['billingPeriods'],
          message: 'billingPeriodEndDateRequired',
        });
      }
    }

    // SUCCESS_FEE validation
    else if (data.type === 'SUCCESS_FEE') {
      if (
        data.claimedPrice == null ||
        Number.isNaN(data.claimedPrice) ||
        data.claimedPrice <= 0
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['claimedPrice'],
          message: 'claimedPriceRequiredForSuccessFee',
        });
      }

      // The success package is an OPTIONAL override — only validate the
      // fields the user actually entered.
      const successOverride = data.successPackage;
      if (successOverride) {
        if (successOverride.advancePayment !== undefined && successOverride.advancePayment < 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['successPackage', 'advancePayment'],
            message: 'advancePaymentMustBeNonNegative',
          });
        }
        if (successOverride.successPayment !== undefined && successOverride.successPayment <= 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['successPackage', 'successPayment'],
            message: 'successPaymentMustBePositive',
          });
        }
      }
    }

    // Percent validation (applies to all types)
    if (data.advancePaymentUnit === 'percent' && data.advancePayment !== undefined) {
      if (data.advancePayment < 0 || data.advancePayment > 100) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['advancePayment'],
          message: 'percentAdvanceMustBeBetween0And100',
        });
      }
    }

    // Billing period validation for HOURLY contracts.
    //
    // INSTANCE is exempt (same as the two checks above): its periods are started
    // and stopped manually after creation, so they are not a schedule that spans
    // the contract — the first one begins whenever work starts and the last one
    // has no relation to the contract's end date. Applying these checks to an
    // INSTANCE contract that has an end date made it unsavable, since no manually
    // started period will ever line up with the contract boundaries.
    if (
      data.type === 'HOURLY' &&
      data.billingPeriodType !== 'INSTANCE' &&
      data.billingPeriods &&
      data.billingPeriods.length > 0 &&
      data.endsAt
    ) {
      const periods = data.billingPeriods;
      const firstPeriod = periods[0];
      const lastPeriod = periods[periods.length - 1];

      if (firstPeriod?.startsAt && data.startsAt) {
        const firstStart = new Date(firstPeriod.startsAt);
        const contractStart = new Date(data.startsAt);
        firstStart.setHours(0, 0, 0, 0);
        contractStart.setHours(0, 0, 0, 0);
        if (firstStart.getTime() !== contractStart.getTime()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['billingPeriods'],
            message: 'billingPeriodsStartMustMatchContractStart',
          });
        }
      }

      if (lastPeriod?.endsAt && data.endsAt) {
        const lastEnd = new Date(lastPeriod.endsAt);
        const contractEnd = new Date(data.endsAt);
        lastEnd.setHours(0, 0, 0, 0);
        contractEnd.setHours(0, 0, 0, 0);
        if (lastEnd.getTime() !== contractEnd.getTime()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['billingPeriods'],
            message: 'billingPeriodsEndMustMatchContractEnd',
          });
        }
      }
    }
  });

export type ContractFormValues = z.infer<typeof contractSchema>;

// Billing period schema — starting an INSTANCE period assigns the period its
// OWN tariff, same as the contract form: `packageId` only binds the optional
// template picker (never sent connected); the nested `package` fields are what
// the backend clones into a single-contract package.
export const billingPeriodSchema = z.object({
  name: z.string().min(3, 'nameMinLength'),
  contractId: emptyToUndefined.pipe(z.coerce.number().int().positive().optional()),
  packageId: emptyToUndefined.pipe(z.coerce.number().int().positive().optional()),
  package: z.object({
    hours: z.preprocess(
      (val) => (val === '' || val == null || Number.isNaN(val) ? undefined : Number(val)),
      z.number({ required_error: 'hoursRequiredAndPositive' }).positive('hoursRequiredAndPositive')
    ),
    price: z.preprocess(
      (val) => (val === '' || val == null || Number.isNaN(val) ? undefined : Number(val)),
      z.number({ required_error: 'priceRequiredAndPositive' }).positive('priceRequiredAndPositive')
    ),
    overTimeHourlyCost: z.preprocess(
      (val) => (val === '' || val == null || Number.isNaN(val) ? undefined : Number(val)),
      z.number({ required_error: 'overtimeCostRequiredAndPositive' }).positive('overtimeCostRequiredAndPositive')
    ),
    rates: z.array(rateSchema).optional().default([]),
  }),
});

export type BillingPeriodFormValues = z.infer<typeof billingPeriodSchema>;
